import type { FirebaseStorage } from 'firebase/storage';
import { readFromCache, saveToCache } from '@/lib/utils/cache-utils';
import { appId } from '@/lib/constants';

const FOOTNOTE_PATHS: Record<string, string> = {
    csb: 'csb/csb-footnotes.json'
};

export interface FootnoteData {
    footnoteMap: Map<string, TranslationFootnote[]>;
    crossRefMap: Map<string, TranslationFootnote[]>;
}

/**
 * Fetch footnotes from Firebase Storage and build an indexed Map for O(1) lookups.
 * Strips the `o` (order) and `l` (empty object) fields to reduce memory footprint.
 * Returns separate maps for textual footnotes and cross-references.
 */
export async function fetchFootnotesFromStorage(
    version: string,
    storage: FirebaseStorage
): Promise<FootnoteData> {
    const cacheKey = `footnotes-v2-${version}`;

    // Check cache first
    const cached = await readFromCache<{
        footnoteMap: [string, TranslationFootnote[]][];
        crossRefMap: [string, TranslationFootnote[]][];
    }>(appId, cacheKey);

    if (cached) {
        return {
            footnoteMap: new Map(cached.footnoteMap),
            crossRefMap: new Map(cached.crossRefMap)
        };
    }

    const path = FOOTNOTE_PATHS[version];
    if (!path) {
        console.warn(`No footnote path configured for version: ${version}`);
        return { footnoteMap: new Map(), crossRefMap: new Map() };
    }

    // Fetch from Firebase Storage using public URL
    const bucket = 'bible-glen.firebasestorage.app';
    const encodedPath = encodeURIComponent(path);
    const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;

    const response = await fetch(url);
    const rawData = await response.json();

    // Build Maps indexed by verse reference
    const footnoteMap = new Map<string, TranslationFootnote[]>();
    const crossRefMap = new Map<string, TranslationFootnote[]>();

    rawData.forEach((item: any) => {
        // Strip `o` and `l` fields
        const footnote: TranslationFootnote = {
            r: item.r,
            t: item.t,
            w: item.w,
            c: item.c,
            a: item.a
        };

        const ref = footnote.r;

        // Items are disjoint: either text or cross-ref
        if (footnote.t) {
            if (!footnoteMap.has(ref)) footnoteMap.set(ref, []);
            footnoteMap.get(ref)!.push(footnote);
        }
        else if (footnote.c) {
            if (!crossRefMap.has(ref)) crossRefMap.set(ref, []);
            crossRefMap.get(ref)!.push(footnote);
        }
    });

    // Cache the result (convert Maps to arrays for serialization)
    saveToCache(appId, cacheKey, {
        footnoteMap: Array.from(footnoteMap.entries()),
        crossRefMap: Array.from(crossRefMap.entries())
    });

    return { footnoteMap, crossRefMap };
}
